import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ChangePassWordTab from './ChangePassWordTab';
import { formatDateTimeWithoutHour, getInfomationAccountFromToken } from '../../utils/helper';
import SettingsTab from './SettingsTab';

import { getAccountInfoByIdAccountAction } from '../../store/reducers/account';
import { Get } from '@app/utils/httpProvider';
import { userManager } from '@app/utils/oidc';
import { objectAccountGender } from '@app/utils/define';
import { ACCOUNT_API, ACCOUNT_EMAIL_API, ACCOUNTAUTHINFO_API } from '@app/utils/apiUrl';

const splitFullName = (fullName = '') => {
    const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return { lastName: '', middleName: '', firstName: '' };
    }
    if (parts.length === 1) {
        return { lastName: '', middleName: '', firstName: parts[0] };
    }
    if (parts.length === 2) {
        return { lastName: parts[0], middleName: '', firstName: parts[1] };
    }
    const lastName = parts[0];
    const firstName = parts[parts.length - 1];
    const middleName = parts.slice(1, -1).join(' ');
    return { lastName, middleName, firstName };
};

const Profile = () => {
    const [activeTab, setActiveTab] = useState('SETTINGS');
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const [myProfile, setMyProfile] = useState(null);
    const [kpiEmail, setKpiEmail] = useState('');

    const itemAccountInfo = useSelector((state) => state.account.itemAccountInfo);

    const loadUserProfile = async () => {
        try {
            const user = await userManager.getUser();
            const tokenAccount = getInfomationAccountFromToken();

            const userId = user?.profile?.sub || user?.profile?.accountId || tokenAccount?.sub;
            if (!userId) return;

            const username = user?.profile?.preferred_username || tokenAccount?.preferred_username || tokenAccount?.name || '';
            const fullNameFromToken = user?.profile?.name || tokenAccount?.name || '';
            const parsedName = splitFullName(fullNameFromToken);

            let authAccount = null;
            try {
                // Thử lấy thông tin chi tiết từ AuthService nếu là admin
                authAccount = await Get(ACCOUNT_API.GET_BY_ID_ACCOUNT, userId, null);
            } catch (e) {
                // Non-admin sẽ bị 403 -> dùng thông tin từ token và kpi c9
            }

            let authInfoItem = null;
            try {
                authInfoItem = await Get(ACCOUNTAUTHINFO_API.GET_ITEM, userId, null);
            } catch (e) {
                // Bỏ qua lỗi nếu không tìm thấy
            }

            let kpiEmailData = null;
            try {
                kpiEmailData = await Get(`${ACCOUNT_EMAIL_API.GET_BY_ACCOUNT_ID}/${userId}`);
                if (kpiEmailData && kpiEmailData.email) {
                    setKpiEmail(kpiEmailData.email);
                }
            } catch (e) {
                // Bỏ qua lỗi
            }

            const authInfo = authAccount?.accountAuthInfo || authInfoItem || {};
            const resolvedLastName = authInfo.lastName || parsedName.lastName || '';
            const resolvedMiddleName = authInfo.middleName || parsedName.middleName || '';
            const resolvedFirstName = authInfo.firstName || parsedName.firstName || '';
            const resolvedEmail = kpiEmailData?.email || authInfo.email || user?.profile?.email || tokenAccount?.email || '';
            const resolvedFullName = `${resolvedLastName} ${resolvedMiddleName} ${resolvedFirstName}`.replace(/\s+/g, ' ').trim() || fullNameFromToken || username;

            setMyProfile({
                id: userId,
                authInfoId: authInfoItem?.id || authAccount?.accountAuthInfo?.id || userId,
                username: authAccount?.username || username,
                fullName: resolvedFullName,
                lastName: resolvedLastName,
                middleName: resolvedMiddleName,
                firstName: resolvedFirstName,
                email: resolvedEmail,
                address: authInfo.address || '',
                dateOfBirth: authInfo.dateOfBirth || null,
                accountGender: authInfo.accountGender || null,
                issuanceDateOfIdentityCard: authInfo.issuanceDateOfIdentityCard || null
            });

            // Tải thông tin chung (đơn vị, chức vụ...) từ App dùng chung
            dispatch(getAccountInfoByIdAccountAction(userId));
        } catch (err) {
            console.error('Error loading profile', err);
        }
    };

    useEffect(() => {
        loadUserProfile();
    }, []);

    const handleProfileUpdated = (newValues) => {
        setMyProfile(prev => {
            const updatedLastName = newValues.lastName || '';
            const updatedMiddleName = newValues.middleName || '';
            const updatedFirstName = newValues.firstName || '';
            const updatedFullName = `${updatedLastName} ${updatedMiddleName} ${updatedFirstName}`.replace(/\s+/g, ' ').trim() || prev?.username;
            return {
                ...prev,
                lastName: updatedLastName,
                middleName: updatedMiddleName,
                firstName: updatedFirstName,
                email: newValues.email || '',
                fullName: updatedFullName
            };
        });
        if (newValues.email) {
            setKpiEmail(newValues.email);
        }
    };

    const toggle = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const renderItems = (items, fields = ['ten', 'name', 'tenDonVi', 'tenChucVu', 'tenChuyenMon']) => {
        if (!Array.isArray(items) || items.length === 0) {
            return <div>Không có dữ liệu</div>;
        }
        return items.map((item, index) => {
            const text = fields
                .map((field) => item?.[field] || item?.[field]?.ten || item?.[field]?.name)
                .find(Boolean);
            return (
                <div key={`item-${index}`}>
                    {text || JSON.stringify(item)}
                </div>
            );
        });
    };

    const renderTrainingItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) {
            return <div>Không có dữ liệu</div>;
        }
        return items.map((item, index) => {
            const text =
                item?.trinhDo?.tenTrinhDo + ' - ' +
                item?.hinhThucDaoTao?.tenHinhThuc + ' - ' +
                item?.nganhDaoTao?.tenNganh + ' - ' +
                formatDateTimeWithoutHour(item?.tuNgay) + ' đến ' +
                formatDateTimeWithoutHour(item?.denNgay) + ' tại ' +
                item?.donViDaoTao?.tenDonViDaoTao;
            return (
                <div key={`training-${index}`}>
                    {text}
                </div>
            );
        });
    };

    return (
        <>
            <section className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1>Tài khoản</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item">
                                    <Link to="/">{t('header.label.home')}</Link>
                                </li>
                                <li className="breadcrumb-item active">
                                    {t('views.user.profile')}
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </section>
            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-3">
                            <div className="card card-primary card-outline">
                                <div className="card-body box-profile">
                                    <div className="text-center">
                                        <img
                                            className="profile-user-img img-fluid img-circle"
                                            src="/img/default-profile.png"
                                            alt="User profile"
                                        />
                                    </div>
                                    <h3 className="profile-username text-center">
                                        {myProfile?.username || ''}
                                    </h3>
                                    <p className="text-muted text-center">
                                        {myProfile?.fullName || ''}
                                    </p>
                                    <ul className="list-group list-group-unbordered mb-3">
                                        <li className="list-group-item">
                                            <b>Tài khoản</b>
                                            <span className="float-right">
                                                {myProfile?.username || ''}
                                            </span>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Họ tên</b>
                                            <span className="float-right">
                                                {myProfile?.fullName || ''}
                                            </span>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Email</b>
                                            <span className="float-right">
                                                {kpiEmail || myProfile?.email || ''}
                                            </span>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Địa chỉ</b>
                                            <span className="float-right">
                                                {myProfile?.address || 'Chưa cập nhật'}
                                            </span>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Ngày sinh</b>
                                            <span className="float-right">
                                                {myProfile?.dateOfBirth
                                                    ? new Date(myProfile.dateOfBirth).toLocaleDateString()
                                                    : 'Chưa cập nhật'}
                                            </span>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Giới tính</b>
                                            <span className="float-right">
                                                {objectAccountGender.find(gender => gender.value === myProfile?.accountGender)?.label || 'Chưa cập nhật'}
                                            </span>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Ngày cấp CCCD</b>
                                            <span className="float-right">
                                                {myProfile?.issuanceDateOfIdentityCard
                                                    ? new Date(myProfile.issuanceDateOfIdentityCard).toLocaleDateString()
                                                    : 'Chưa cập nhật'}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-9">
                            <div className="card">
                                <div className="card-header p-2">
                                    <ul className="nav nav-pills">
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link ${activeTab === 'SETTINGS'
                                                    ? 'active'
                                                    : ''
                                                    }`}
                                                onClick={() =>
                                                    toggle('SETTINGS')
                                                }
                                            >
                                                Cài đặt tài khoản
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link ${activeTab ===
                                                    'CHANGEPASSWORD'
                                                    ? 'active'
                                                    : ''
                                                    }`}
                                                onClick={() =>
                                                    toggle('CHANGEPASSWORD')
                                                }
                                            >
                                                Đổi mật khẩu
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                <div className="card-body">
                                    <div className="tab-content">
                                        <SettingsTab
                                            isActive={activeTab === 'SETTINGS'}
                                            profile={myProfile}
                                            onUpdated={handleProfileUpdated}
                                        />
                                        <ChangePassWordTab
                                            isActive={
                                                activeTab === 'CHANGEPASSWORD'
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="card-body">
                                    <h5>Thông tin bổ sung</h5>
                                    <ul className="list-group list-group-unbordered mb-3">
                                        <li className="list-group-item">
                                            <b>Đơn vị</b>
                                            <div className="float-right text-right">
                                                {renderItems(itemAccountInfo?.donVis, ['tenDonVi', 'name', 'ten'])}
                                            </div>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Chức vụ</b>
                                            <div className="float-right text-right">
                                                {renderItems(itemAccountInfo?.chucVus, ['tenChucVu', 'name', 'ten'])}
                                            </div>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Chuyên môn</b>
                                            <div className="float-right text-right">
                                                {renderItems(itemAccountInfo?.chuyenMons, ['tenChuyenMon', 'name', 'ten'])}
                                            </div>
                                        </li>
                                        <li className="list-group-item">
                                            <b>Quá trình đào tạo</b>
                                            <div className="float-right text-right">
                                                {renderTrainingItems(itemAccountInfo?.quaTrinhDaoTaos)}
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Profile;
